import { useABAC } from '@adornis/abac/client/useABAC.js';
import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { CheckboxInput } from '@adornis/formfield/fields/checkbox-input.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useEffect, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { subscribeScopedAvailabilities } from '../../api/scoped-queries.js';
import { SmartTeamsAvailability, type SmartTeamsFieldEngineer } from '../../db/index.js';
import { accessController } from '../../features/permissions/init-permissions.js';
import { SmartTeamsPermission } from '../../features/permissions/roles-and-permissions.js';
import { formatClockTime, formatDate } from '../date-format.js';

const CreateFEAvailabilityForm = stateful(
  (props: {
    fieldEngineer: SmartTeamsFieldEngineer;
    onSubmit: (data: SmartTeamsAvailability) => void;
    onCancel: () => void;
  }) => {
    const [availability] = useState(
      new SmartTeamsAvailability({
        fieldEngineerId: props.fieldEngineer._id,
        startTime: '08:00',
        endTime: '17:00',
        useDefaultLocation: true,
      }),
    );
    const { document, field, validate } = useForm<SmartTeamsAvailability>(availability, {
      validator: SmartTeamsAvailability.prototype.validator.functions,
    });

    return html`
      <div class="flex flex-col gap-4">
        <h3 class="text-lg font-bold">New availability for ${props.fieldEngineer.name}</h3>

        <label class="form-control w-full">
          <div class="label"><span class="label-text">Date *</span></div>
          ${DateInput({ ...field('date'), type: 'date' })}
        </label>

        <div class="grid grid-cols-2 gap-4">
          <label class="form-control w-full">
            <div class="label"><span class="label-text">Start time *</span></div>
            ${TextInput({ ...field('startTime'), placeholder: '08:00' })}
          </label>
          <label class="form-control w-full">
            <div class="label"><span class="label-text">End time *</span></div>
            ${TextInput({ ...field('endTime'), placeholder: '17:00' })}
          </label>
        </div>

        ${CheckboxInput({ ...field('useDefaultLocation'), label: 'Use default location' })}

        <div class="flex justify-end gap-2 mt-2">
          <button class="btn btn-ghost" @click=${props.onCancel}>Cancel</button>
          <button
            class="btn btn-primary"
            @click=${async () => {
              const error = await validate();
              if (error) return;
              props.onSubmit(document);
            }}
          >
            Create
          </button>
        </div>
      </div>
    `;
  },
);

/**
 * Shows availabilities for a specific Field Engineer
 */
export const FEAvailabilitySection = stateful(({ fieldEngineer }: { fieldEngineer: SmartTeamsFieldEngineer }) => {
  const { can: canCreate } = useABAC(
    accessController,
    createPermissionPayload(SmartTeamsPermission.AvailabilityCreate),
  );
  const { can: canDelete } = useABAC(
    accessController,
    createPermissionPayload(SmartTeamsPermission.AvailabilityDelete),
  );

  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  // Load availabilities for this FE (backend-scoped by FE ID)
  const availabilitiesQuery = useQuery(
    () =>
      subscribeScopedAvailabilities(fieldEngineer._id)({
        date: 1,
        endTime: 1,
        startTime: 1,
        fieldEngineerId: 1,
        location: { city: 1, country: 1, lat: 1, lng: 1, number: 1, street: 1, zipCode: 1 },
        notes: 1,
        useDefaultLocation: 1,
      }),
    [refreshKey, fieldEngineer._id],
  );

  const availabilities = ((availabilitiesQuery.value ?? []) as SmartTeamsAvailability[]).sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : 0;
    const dateB = b.date ? new Date(b.date).getTime() : 0;
    return dateA - dateB;
  });

  // Derive the set of months that have at least one availability.
  const availableMonths = useMemo((): [string[]] => {
    const monthSet = new Set<string>();
    for (const av of availabilities) {
      if (!av.date) continue;
      const date = new Date(av.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthSet.add(monthKey);
    }
    return [[...monthSet].sort()];
  }, [availabilities]);

  // Default to the current month, falling back to the first one with data.
  useEffect(() => {
    if (selectedMonth && availableMonths.includes(selectedMonth)) return;
    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    if (availableMonths.includes(currentMonthKey)) {
      setSelectedMonth(currentMonthKey);
    } else if (availableMonths.length > 0) {
      setSelectedMonth(availableMonths[0] ?? null);
    }
  }, [availableMonths]);

  // Availabilities filtered to the currently selected month.
  const filteredAvailabilities = useMemo((): [SmartTeamsAvailability[]] => {
    if (!selectedMonth) return [[]];
    return [
      availabilities.filter(av => {
        if (!av.date) return false;
        const date = new Date(av.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        return monthKey === selectedMonth;
      }),
    ];
  }, [availabilities, selectedMonth]);

  // Month-picker label helper.
  const formatMonth = (key: string) =>
    new Date(`${key}-01`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const currentIndex = selectedMonth ? availableMonths.indexOf(selectedMonth) : -1;
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < availableMonths.length - 1;

  const refresh = () => setRefreshKey(refreshKey + 1);

  const handleCreateAvailability = async () => {
    const availability = await showDialog<SmartTeamsAvailability | null>({
      modalBoxClasses: 'max-w-md',
      content: (ok, cancel) => CreateFEAvailabilityForm({ fieldEngineer, onSubmit: ok, onCancel: () => cancel() }),
    }).catch(() => null);

    if (!availability) return;

    await guardDialog(
      async () => {
        await availability.create?.();
      },
      {
        loadingText: 'Creating availability…',
        successText: 'Availability created.',
        errorMessagePrefix: 'Availability could not be created',
      },
    );
    refresh();
  };

  const handleDeleteAvailability = async (availability: SmartTeamsAvailability) => {
    await guardDialog(
      async () => {
        await availability.remove?.();
      },
      {
        loadingText: 'Deleting availability…',
        successText: 'Availability deleted.',
        errorMessagePrefix: 'Availability could not be deleted',
      },
    );
    refresh();
  };

  const isLoading = availabilitiesQuery.loading;

  return html`
    <div class="flex flex-col gap-3">
      ${availabilities.length === 0
        ? html`
            <div class="flex items-center justify-between bg-base-200 rounded-lg p-4">
              <div class="flex items-center gap-3 text-base-content/60">
                <span class="icon-[mdi--calendar-blank-outline] text-2xl"></span>
                <span class="text-sm mr-2"> No availability entries created yet </span>
                ${isLoading ? html`<span class="loading loading-spinner loading-xs"></span>` : nothing}
              </div>
              ${canCreate
                ? html`
                    <button class="btn btn-primary btn-sm" @click=${handleCreateAvailability}>
                      <span class="icon-[mdi--plus] text-lg"></span>
                      Add
                    </button>
                  `
                : nothing}
            </div>
          `
        : html`
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <span class="text-sm text-base-content/70"
                  >${availabilities.length} availabilit${availabilities.length !== 1 ? 'ies' : 'y'}</span
                >
                ${isLoading ? html`<span class="loading loading-spinner loading-xs text-primary"></span>` : nothing}
              </div>
              ${canCreate
                ? html`
                    <button class="btn btn-primary btn-sm" @click=${handleCreateAvailability}>
                      <span class="icon-[mdi--plus] text-lg"></span>
                      Add
                    </button>
                  `
                : nothing}
            </div>
            <div class="bg-base-200 rounded-lg">
              <!-- Filter header -->
              <div class="flex items-center justify-between p-4 border-b border-base-300">
                <div class="flex items-center gap-2">
                  <button
                    class="btn btn-ghost btn-sm btn-square"
                    ?disabled=${!canGoPrev}
                    @click=${() => setSelectedMonth(availableMonths[currentIndex - 1] ?? null)}
                  >
                    <span class="icon-[mdi--chevron-left] text-lg"></span>
                  </button>
                  <select
                    class="select select-bordered select-sm min-w-[180px]"
                    .value=${selectedMonth ?? ''}
                    @change=${(e: Event) => setSelectedMonth((e.target as HTMLSelectElement).value)}
                  >
                    ${availableMonths.map(
                      m => html` <option value=${m} ?selected=${selectedMonth === m}>${formatMonth(m)}</option> `,
                    )}
                  </select>
                  <button
                    class="btn btn-ghost btn-sm btn-square"
                    ?disabled=${!canGoNext}
                    @click=${() => setSelectedMonth(availableMonths[currentIndex + 1] ?? null)}
                  >
                    <span class="icon-[mdi--chevron-right] text-lg"></span>
                  </button>
                </div>
                <span class="badge badge-sm">${filteredAvailabilities.length}</span>
              </div>
              <!-- Table -->
              <div class="p-4">
                <div class="overflow-x-auto">
                  <table class="table table-sm w-full">
                    <thead>
                      <tr>
                        <th class="w-28">Date</th>
                        <th class="w-32">Time</th>
                        <th>Location</th>
                        ${canDelete ? html`<th class="w-12"></th>` : nothing}
                      </tr>
                    </thead>
                    <tbody>
                      ${filteredAvailabilities.map(
                        av => html`
                          <tr>
                            <td>${av.date ? formatDate(av.date) : '-'}</td>
                            <td>${formatClockTime(av.startTime)} - ${formatClockTime(av.endTime)}</td>
                            <td>
                              ${av.useDefaultLocation
                                ? html` <span class="badge badge-ghost badge-sm"> Default </span> `
                                : (av.location?.city ?? '-')}
                            </td>
                            ${canDelete
                              ? html`
                                  <td>
                                    <button
                                      class="btn btn-ghost btn-xs text-error"
                                      @click=${() => handleDeleteAvailability(av)}
                                    >
                                      <span class="icon-[mdi--delete] text-sm"></span>
                                    </button>
                                  </td>
                                `
                              : nothing}
                          </tr>
                        `,
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          `}
    </div>
  `;
});
